import React, { useEffect, useState, useRef } from "react";
import { Table, Tag, Popover, message, Modal, DatePicker, Spin } from "antd";

import { FiPlus } from "react-icons/fi";
import { AiOutlineEye, AiOutlineDelete } from "react-icons/ai";
import { FiDownload } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import TaxInvoicePage from "./TaxInvoicePage";
import ReactDOM from "react-dom/client";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { LiaEditSolid } from "react-icons/lia";
import { AiOutlinePlusCircle } from "react-icons/ai";

import "./taxinvoice.css";
import Instance from "../../AxiosConfig";
import { Form } from "react-bootstrap";

const TaxInvoiceTable = () => {
  const navigate = useNavigate();
  const [invoiceData, setInvoiceData] = useState([]);
  const [loading, setLoading] = useState(false);
  const hiddenContainerRef = useRef(null);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [currency, setCurrency] = useState("All");
  const [fromDate, setFromDate] = useState(null);
  const [processedInvoices, setProcessedInvoices] = useState(new Set());
  const [downloadingId, setDownloadingId] = useState(null);

  const [toDate, setToDate] = useState(null);
  const debounceTimer = useRef(null);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 50,
    total: 0,
  });
  const handleClearFilters = () => {
    setSearch("");
    setStatus("All");
    setCurrency("All");
    setFromDate(null);
    setToDate(null);
  };
  // Fetch filtered data with debounce
  const fetchFilteredData = async (
    page = pagination.current,
    limit = pagination.pageSize,
  ) => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");

      // Build query parameters
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", limit);

      if (search.trim()) {
        params.append("search", search.trim());
      }

      if (status && status !== "All") {
        params.append("status", status);
      }

      if (currency && currency !== "All") {
        params.append("currency", currency);
      }

      if (fromDate) {
        params.append("startDate", fromDate);
      }

      if (toDate) {
        params.append("endDate", toDate);
      }

      const queryString = params.toString();
      const endpoint = queryString
        ? `/invoices/filter/search?${queryString}`
        : `/invoices`;

      const response = await Instance.get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        const data = response?.data || {};
        const invoices = data.invoices || [];

        setInvoiceData(invoices);

        // 👇 THIS IS THE MAIN FIX
        if (data.total) {
          setPagination((prev) => ({
            ...prev,
            total: data.total,
            current: data.page || page,
          }));
        } else {
          // normal API (no pagination)
          setPagination((prev) => ({
            ...prev,
            total: invoices.length,
            current: 1,
          }));
        }
      } else {
        message.error("Failed to fetch invoice data");
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      message.error("Error fetching invoices");
    } finally {
      setLoading(false);
    }
  };

  // Debounced filter function
  const debouncedFetch = () => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      fetchFilteredData();
    }, 500); // 500ms delay
  };

  // Initial load
  useEffect(() => {
    fetchFilteredData();
  }, []);

  // Listen to filter changes
  useEffect(() => {
    debouncedFetch();

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [search, status, currency, fromDate, toDate]);

  const handleTaxInvoiceData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await Instance.get("/invoices", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200 || response.status === 201) {
        const invoices = response?.data?.invoices || [];
        setInvoiceData(invoices);
        console.log("Fetched invoices:", invoices);
      } else {
        message.error("Failed to fetch invoice data");
      }
    } catch (error) {
      console.error("Error fetching invoices:", error);
      message.error("Error fetching invoices");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    handleTaxInvoiceData();
  }, []);

  const handleAddInvoice = () => {
    navigate("/add-tax-invoice");
  };

  // PDF DOWNLOAD - WORKS 100% WITH DATA + CORRECT FILENAME
const handleDownloadPDF = async (record, termId) => {
  try {
    setDownloadingId(record._id);

    // ✅ ADD HERE (before rendering)
    document.body.classList.add("pdf-mode");

    const container = hiddenContainerRef.current;
    if (!container) return;

    const tempDiv = document.createElement("div");
    container.appendChild(tempDiv);

    const root = ReactDOM.createRoot(tempDiv);
    root.render(<TaxInvoicePage id={record._id} termId={termId} />);

    await new Promise((resolve) => {
      const interval = setInterval(() => {
        const hasData =
          tempDiv.querySelector(".invoice-title") ||
          tempDiv.innerHTML.includes("TAX INVOICE");
        if (hasData) {
          clearInterval(interval);
          setTimeout(resolve, 600);
        }
      }, 200);
    });

    const canvas = await html2canvas(tempDiv, {
      scale: 1, // ✅ better quality
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/jpeg", 1);

    const pdf = new jsPDF("p", "mm", "a4");

    // ✅ SIMPLE (no scaling issues now)
pdf.addImage(imgData, "JPEG", 0, 0, 210, 297);
    const fileName = termId
      ? `${record.invoiceNo}-${termId.slice(-6)}.pdf`
      : `${record.invoiceNo}.pdf`;

    pdf.save(fileName);

    root.unmount();
    container.removeChild(tempDiv);

    message.success("Invoice PDF downloaded successfully!");
  } catch (err) {
    console.error(err);
    message.error("Failed to generate invoice PDF.");
  } finally {
    setDownloadingId(null);

    // ✅ REMOVE HERE (VERY IMPORTANT)
    document.body.classList.remove("pdf-mode");
  }
};

  const handleDeleteInvoice = async (id) => {
    Modal.confirm({
      title: (
        <span style={{ fontWeight: 600, color: "#000", fontSize: "16px" }}>
          Delete this invoice?
        </span>
      ),
      icon: (
        <ExclamationCircleOutlined
          style={{ color: "#ff4d4f", fontSize: "20px" }}
        />
      ),
      content: (
        <span style={{ color: "#555", fontSize: "14px" }}>
          This action cannot be undone. Are you sure you want to proceed?
        </span>
      ),
      okText: "Delete",
      cancelText: "Cancel",
      okType: "danger",
      centered: true,
      maskClosable: true,
      closable: true,
      okButtonProps: {
        style: {
          backgroundColor: "#ff4d4f",
          borderColor: "#ff4d4f",
          fontWeight: 500,
          color: "white",
        },
      },
      cancelButtonProps: { style: { borderRadius: 6, fontWeight: 500 } },
      onOk: async () => {
        try {
          const token = localStorage.getItem("token");
          const response = await Instance.delete(`/invoices/${id}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          if (response.status === 200 || response.status === 204) {
            setInvoiceData((prev) =>
              prev.filter((invoice) => invoice._id !== id),
            );
            message.success("Invoice deleted successfully!");
          }
        } catch (error) {
          console.error("Error deleting invoice:", error);
          const errorMsg =
            error.response?.data?.message || "Failed to delete invoice";
          message.error(errorMsg);
        }
      },
    });
  };

  const columns = [
    {
      title: "Invoice No",
      dataIndex: "invoiceNo",
      key: "invoiceNo",
      fixed: "left",
      render: (val) => <strong>{val}</strong>,
      sorter(a, b) {
        return a.invoiceNo.localeCompare(b.invoiceNo);
      },
      defaultSortOrder: "descend",
    },
    {
      title: "Company",
      dataIndex: "companyName",
      key: "companyName",
    },
    {
      title: "Agent",
      key: "agentName",
      render: (_, record) => record?.agentId?.agentName || "—",
    },
    {
      title: "Currency",
      dataIndex: "currency",
      key: "currency",
      render: (currency) => (
        <Tag
          color={currency === "INR" ? "green" : "gold"}
          style={{ fontWeight: 500 }}
        >
          {currency}
        </Tag>
      ),
    },
    {
      title: "Total Closure",
      dataIndex: "baseClosureAmount",
      key: "baseClosureAmount",
      render: (val, record) =>
        `${record.currency} ${(val || 0).toLocaleString()}`,
    },
     {
      title: "Term Name",
      render: (val, record) =>
        `${record.terms[0]?.termName}`,
    },
    {
      title: "Term Amount",
      render: (val, record) =>
        `${record.currency} ${record.terms[0]?.baseAmount}`,
    },
    {
      title: "Money Received",
      dataIndex: "moneyReceived",
      key: "moneyReceived",
      render: (val, record) => (val ? `INR ${val.toLocaleString()}` : "—"),
    },
    {
      title: "Pending Payment (INR)",
      dataIndex: "PendingPaymentInINR",
      key: "PendingPaymentInINR",
      render: (val, record) => (
        <span style={{ color: "#ff4d4f", fontWeight: 600 }}>
          {record.currency} {Number(val || 0).toLocaleString()}
        </span>
      ),
    },
    {
      title: "Payment Status",
      dataIndex: "status",
      render: (_, record) => {
        const val = record?.terms?.[0]?.status || "Pending";

        return (
          <Tag
            color={val === "Paid" ? "green" : "red"}
            style={{ fontWeight: 500 }}
          >
            {val}
          </Tag>
        );
      },
    },
    {
      title: "Invoice Date",
      dataIndex: "InvoiceDate",
      key: "createdAt",
      render: (val) => dayjs(val).format("DD MMM YYYY"),
    },
    {
      title: "Action",
      render: (_, record) => {
        return (
          <div className="d-flex gap-2 justify-content-end">
            {record?.isLatest && record?.isPendingPayment && (
              <Popover content="Add Invoice">
                <AiOutlinePlusCircle
                  size={18}
                  style={{ cursor: "pointer", color: "purple" }}
                  onClick={() =>
                    navigate(`/add-initial-tax-invoice/${record._id}`)
                  }
                />
              </Popover>
            )}

            <Popover content="View Invoice">
              <AiOutlineEye
                size={18}
                style={{ cursor: "pointer", color: "#1890ff" }}
                onClick={() => navigate(`/tax-invoice-page/${record._id}`)}
              />
            </Popover>

            <Popover content="Download PDF">
              {downloadingId === record._id ? (
                <Spin size="small" />
              ) : (
                <FiDownload
                  size={18}
                  style={{ cursor: "pointer", color: "#52c41a" }}
                  onClick={() => handleDownloadPDF(record, null)}
                />
              )}
            </Popover>

            <Popover content="Delete">
              <AiOutlineDelete
                size={18}
                style={{ cursor: "pointer", color: "#ff4d4f" }}
                onClick={() => handleDeleteInvoice(record._id)}
              />
            </Popover>

            <Popover content="Edit">
              <LiaEditSolid
                size={18}
                style={{ cursor: "pointer", color: "#faad14" }}
                onClick={() => navigate(`/edit-tax-invoice/${record._id}`)}
              />
            </Popover>
          </div>
        );
      },
    },
  ];

  return (
    <>
      <div className="filter-card p-3">
        <Form>
          <div className="row g-3">
            <div className="col-md-3">
              <Form.Group>
                <Form.Label>Search</Form.Label>
                <Form.Control
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search by company or invoice no..."
                />
              </Form.Group>
            </div>

            {/* <div className="col-md-2">
                  <Form.Group>
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                    >
                      <option>All</option>
                      <option>Active</option>
                      <option>Closed</option>
                      <option>Lost</option>
                    </Form.Select>
                  </Form.Group>
                </div> */}

            <div className="col-md-2">
              <Form.Group>
                <Form.Label>Currency</Form.Label>
                <Form.Select
                  value={currency}
                  onChange={(e) => setCurrency(e.target.value)}
                >
                  <option>All</option>
                  <option>INR</option>
                  <option>USD</option>
                  <option>EUR</option>
                  <option>GBP</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-2">
              <Form.Group>
                <Form.Label>From Date</Form.Label>
                <DatePicker
                  className="form-control"
                  value={fromDate ? dayjs(fromDate) : null}
                  onChange={(date) =>
                    setFromDate(date ? date.format("YYYY-MM-DD") : null)
                  }
                  format="YYYY-MM-DD"
                />
              </Form.Group>
            </div>

            <div className="col-md-2">
              <Form.Group>
                <Form.Label>To Date</Form.Label>
                <DatePicker
                  className="form-control"
                  value={toDate ? dayjs(toDate) : null}
                  onChange={(date) =>
                    setToDate(date ? date.format("YYYY-MM-DD") : null)
                  }
                  format="YYYY-MM-DD"
                />
              </Form.Group>
            </div>

            <div className="col-md-1 d-flex align-items-end">
              <button
                type="button"
                className="clear-btn-company w-100"
                onClick={handleClearFilters}
                style={{ height: "38px" }}
              >
                Clear
              </button>
            </div>
          </div>
        </Form>

        {(search ||
          status !== "All" ||
          currency !== "All" ||
          fromDate ||
          toDate) && (
          <div className="mt-3">
            <small className="text-muted">
              {loading
                ? "Loading filtered results..."
                : `Showing ${invoiceData.length} result(s)`}
            </small>
          </div>
        )}
      </div>

      <div className="taxinvoice-wrapper mt-4">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 style={{ fontWeight: "600" }}>Tax Invoices</h2>
          <button
            className="add-certification-btn"
            onClick={handleAddInvoice}
            style={{
              backgroundColor: "#1890ff",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              padding: "6px 16px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
            }}
          >
            <FiPlus /> Add New Invoice
          </button>
        </div>

        <Table
          columns={columns}
          dataSource={invoiceData}
          loading={loading}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: false,
          }}
          onChange={(pag) => {
            setPagination(pag);
            fetchFilteredData(pag.current, pag.pageSize);
          }}
          scroll={{ x: "max-content" }}
          bordered
          rowKey={(record) => record._id}
        />

        <div
          ref={hiddenContainerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            opacity: 0,
            pointerEvents: "none",
          }}
        />
      </div>
    </>
  );
};

export default TaxInvoiceTable;
